import type { SecondaryAnalysisFilter } from 'api/jobs';
import { JobType } from 'interfaces/job';
import { concat, filter, first, includes, isEmpty, size, uniq } from 'lodash';

export const SECONDARY_ANALYSIS_JOB_TYPE_FILTER = '__secondary_analysis__';
export const CALCULATE_FEATURES_NON_SECONDARY_JOB_TYPE_FILTER = '__calculate_features_non_secondary__';

const calculateFeaturesJobType = JobType.PostProcessingAndCalculateFeatures;

interface JobTypeFilters {
  types?: string[];
  orchestrationIds?: string[];
  primaryRunOrchestrationIds?: string[];
  secondaryAnalysisFilter?: SecondaryAnalysisFilter;
}

// The UI exposes primary Calculate Features and Secondary Analysis as separate choices,
// but the API represents both with the calculate_features job type plus this discriminator.
export const mapUiJobTypeFiltersToApiFilters = (
  types?: string[]
): { types?: JobType[]; secondaryAnalysisFilter?: SecondaryAnalysisFilter } => {
  if (isEmpty(types)) {
    return {};
  }

  const hasSecondaryAnalysisOption = includes(types, SECONDARY_ANALYSIS_JOB_TYPE_FILTER);
  const hasNonSecondaryCalculateFeaturesOption = includes(types, CALCULATE_FEATURES_NON_SECONDARY_JOB_TYPE_FILTER);

  const selectedJobTypes = concat(
    filter(
      uniq(types),
      (type): type is JobType =>
        type !== SECONDARY_ANALYSIS_JOB_TYPE_FILTER && type !== CALCULATE_FEATURES_NON_SECONDARY_JOB_TYPE_FILTER
    ),
    hasSecondaryAnalysisOption || hasNonSecondaryCalculateFeaturesOption ? [calculateFeaturesJobType] : []
  );
  const hasOnlyCalculateFeaturesTypeSelected =
    size(selectedJobTypes) === 1 && first(selectedJobTypes) === calculateFeaturesJobType;

  if (hasSecondaryAnalysisOption && !hasNonSecondaryCalculateFeaturesOption && hasOnlyCalculateFeaturesTypeSelected) {
    return {
      types: selectedJobTypes,
      secondaryAnalysisFilter: 'secondary_latest_attempt_per_slide',
    };
  }

  if (!hasSecondaryAnalysisOption && hasNonSecondaryCalculateFeaturesOption && hasOnlyCalculateFeaturesTypeSelected) {
    return {
      types: selectedJobTypes,
      secondaryAnalysisFilter: 'primary',
    };
  }

  return {
    types: selectedJobTypes,
  };
};

// Shared URL state must use the canonical API shape because both Jobs and Procedures consume it;
// UI-only sentinel values would fail backend enum validation if they reached either request.
export const mapUiJobsFiltersToApiFilters = <FiltersType extends JobTypeFilters>(uiFilters: FiltersType) => {
  const hasUiJobType =
    includes(uiFilters?.types, SECONDARY_ANALYSIS_JOB_TYPE_FILTER) ||
    includes(uiFilters?.types, CALCULATE_FEATURES_NON_SECONDARY_JOB_TYPE_FILTER);

  if (!hasUiJobType) {
    return uiFilters;
  }

  const { types, secondaryAnalysisFilter } = mapUiJobTypeFiltersToApiFilters(uiFilters?.types);
  const shouldUsePrimaryRunOrchestrationIdsFilter =
    secondaryAnalysisFilter === 'secondary_all_attempts' ||
    secondaryAnalysisFilter === 'secondary_latest_attempt_per_slide';

  return {
    ...uiFilters,
    types,
    secondaryAnalysisFilter,
    orchestrationIds: shouldUsePrimaryRunOrchestrationIdsFilter ? undefined : uiFilters?.orchestrationIds,
    primaryRunOrchestrationIds: shouldUsePrimaryRunOrchestrationIdsFilter ? uiFilters?.orchestrationIds : undefined,
  };
};

// Convert canonical URL filters back to the UI's two selectable choices without changing
// API modes, such as secondary_all_attempts, that this control cannot represent exactly.
export const mapApiJobsFiltersToUiFilters = <FiltersType extends JobTypeFilters>(apiFilters: FiltersType) => {
  if (!includes(apiFilters?.types, calculateFeaturesJobType)) {
    return apiFilters;
  }

  const typesWithoutCalculateFeatures = filter(apiFilters.types, (type) => type !== calculateFeaturesJobType);
  let calculateFeaturesUiTypes: string[];

  if (apiFilters.secondaryAnalysisFilter === 'primary') {
    calculateFeaturesUiTypes = [CALCULATE_FEATURES_NON_SECONDARY_JOB_TYPE_FILTER];
  } else if (apiFilters.secondaryAnalysisFilter === 'secondary_latest_attempt_per_slide') {
    calculateFeaturesUiTypes = [SECONDARY_ANALYSIS_JOB_TYPE_FILTER];
  } else if (apiFilters.secondaryAnalysisFilter) {
    return apiFilters;
  } else {
    calculateFeaturesUiTypes = [CALCULATE_FEATURES_NON_SECONDARY_JOB_TYPE_FILTER, SECONDARY_ANALYSIS_JOB_TYPE_FILTER];
  }

  return {
    ...apiFilters,
    types: concat(typesWithoutCalculateFeatures, calculateFeaturesUiTypes),
    orchestrationIds: apiFilters.primaryRunOrchestrationIds ?? apiFilters.orchestrationIds,
    primaryRunOrchestrationIds: undefined,
    secondaryAnalysisFilter: undefined,
  };
};
